import { useState, ReactNode } from 'react'

export interface Tab {
  id: string
  label: string
  icon?: string
}

interface TabbedSectionProps {
  title: string
  icon?: string
  tabs: Tab[]
  defaultTab?: string
  children: (activeTab: string) => ReactNode
}

export function TabbedSection({
  title,
  icon,
  tabs,
  defaultTab,
  children,
}: TabbedSectionProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id)

  return (
    <div className="section-card">
      {/* Header */}
      <div className="section-header">
        {icon && <span>{icon}</span>}
        <span>{title}</span>
      </div>

      {/* Tabs */}
      <div className="tab-list">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'tab-active' : 'tab'}
          >
            {tab.icon && <span className="mr-1">{tab.icon}</span>}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="animate-fade-in">{children(activeTab)}</div>
    </div>
  )
}

// Simpler version for cases where content is pre-rendered
interface SimpleTabbedSectionProps {
  title: string
  icon?: string
  tabs: (Tab & { content: ReactNode })[]
  defaultTab?: string
}

export function SimpleTabbedSection({
  title,
  icon,
  tabs,
  defaultTab,
}: SimpleTabbedSectionProps) {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id)

  const activeContent = tabs.find((t) => t.id === activeTab)?.content

  return (
    <div className="section-card">
      {/* Header */}
      <div className="section-header">
        {icon && <span>{icon}</span>}
        <span>{title}</span>
      </div>

      {/* Tabs */}
      <div className="tab-list">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'tab-active' : 'tab'}
          >
            {tab.icon && <span className="mr-1">{tab.icon}</span>}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div key={activeTab} className="animate-fade-in">
        {activeContent}
      </div>
    </div>
  )
}

// Multi-row tabs version (for Traffic Sources section with two rows of tabs)
interface MultiRowTabbedSectionProps {
  title: string
  icon?: string
  tabRows: Tab[][]
  defaultTab?: string
  children: (activeTab: string) => ReactNode
}

export function MultiRowTabbedSection({
  title,
  icon,
  tabRows,
  defaultTab,
  children,
}: MultiRowTabbedSectionProps) {
  const allTabs = tabRows.flat()
  const [activeTab, setActiveTab] = useState(defaultTab || allTabs[0]?.id)

  return (
    <div className="section-card">
      {/* Header */}
      <div className="section-header">
        {icon && <span>{icon}</span>}
        <span>{title}</span>
      </div>

      {/* Tab Rows */}
      {tabRows.map((row, rowIndex) => (
        <div key={rowIndex} className={`tab-list ${rowIndex > 0 ? 'border-t-0' : ''}`}>
          {row.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? 'tab-active' : 'tab'}
            >
              {tab.icon && <span className="mr-1">{tab.icon}</span>}
              {tab.label}
            </button>
          ))}
        </div>
      ))}

      {/* Content */}
      <div key={activeTab} className="animate-fade-in">
        {children(activeTab)}
      </div>
    </div>
  )
}
